import React, { useState, useEffect, useRef } from 'react'
import { api, formatDate, formatDateTime } from '../../utils/api'
import { downloadCsv } from '../../utils/csv.js'
import {
  STATUS_OPTIONS,
  statusLabel,
  hasLastEmail,
  lastEmailLabel,
  d1ToIso,
  buildAbandonedTradeFormsCsv,
} from './tradeFormCaptureRows.js'
import DealLink from '../shared/DealLink.jsx'

const ENDPOINT = '/api/marketing/abandoned-trade-forms'
const PAGE_SIZE = 100
const DEBOUNCE_MS = 300
const CSV_FILENAME = 'abandoned-trade-forms.csv'

/**
 * Marketing > Abandoned trade forms.
 *
 * Everybody who gave their name and email on step 1 of the trade form, whether
 * they went on to send a quote request, and what the chase (TS-0 to TS-4) has
 * sent them. Converted rows link to the quote they became.
 */
function AbandonedTradeForms() {
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [offset, setOffset] = useState(0)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)

  // Every load carries a sequence number, so a slow early request cannot land
  // after a fast later one and repaint the table with stale rows.
  const requestSeq = useRef(0)

  useEffect(() => {
    const timer = setTimeout(() => { setQ(search.trim()); setOffset(0) }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => { load() }, [status, q, from, to, offset])

  function filterParams(extra = {}) {
    const params = new URLSearchParams({ status, ...extra })
    if (q) params.set('q', q)
    if (from) params.set('from', from)
    if (to) params.set('to', to)
    return params
  }

  async function load() {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await api.get(`${ENDPOINT}?${filterParams({ limit: PAGE_SIZE, offset })}`)
      if (seq !== requestSeq.current) return
      setRows(res.items || [])
      setTotal(res.total || 0)
      setTruncated(res.truncated === true)
    } catch (e) {
      if (seq !== requestSeq.current) return
      setError(e.message || 'Could not load abandoned trade forms')
      setRows([])
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  async function exportCsv() {
    setExporting(true)
    try {
      const res = await api.get(`${ENDPOINT}/export?${filterParams()}`)
      downloadCsv(buildAbandonedTradeFormsCsv(res.items || []), CSV_FILENAME)
    } catch (e) {
      setError(e.message || 'Could not export abandoned trade forms')
    } finally {
      setExporting(false)
    }
  }

  function changeFilter(setter) {
    return (event) => { setter(event.target.value); setOffset(0) }
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-section">
          <div className="filter-group">
            <label className="filter-label" htmlFor="atf-status">Status</label>
            <select id="atf-status" className="form-input" value={status} onChange={changeFilter(setStatus)} aria-label="Status">
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="filter-group filter-group-wide">
            <label className="filter-label" htmlFor="atf-search">Search</label>
            <input
              id="atf-search"
              className="form-input"
              type="search"
              placeholder="Name or email"
              aria-label="Search name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label" htmlFor="atf-from">Captured from</label>
            <input id="atf-from" className="form-input" type="date" aria-label="Captured from" value={from} onChange={changeFilter(setFrom)} />
          </div>
          <div className="filter-group">
            <label className="filter-label" htmlFor="atf-to">Captured to</label>
            <input id="atf-to" className="form-input" type="date" aria-label="Captured to" value={to} onChange={changeFilter(setTo)} />
          </div>
        </div>
        <div className="filter-actions">
          <span className="text-sm text-muted">{total} {total === 1 ? 'capture' : 'captures'}</span>
          <button className="btn btn-secondary" onClick={exportCsv} disabled={exporting || total === 0}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      {truncated && (
        <div className="text-sm text-muted" role="status" style={{ marginBottom: 12 }}>
          Showing the first 5,000 matches. Narrow the dates to see the rest.
        </div>
      )}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        {renderBody({ loading, error, rows, onRetry: load })}
      </div>

      {total > PAGE_SIZE && (
        <div className="filter-actions-right" style={{ marginTop: 'var(--space-4)' }}>
          <button className="btn btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Previous
          </button>
          <span className="text-sm text-muted">
            {offset + 1} to {Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <button className="btn btn-secondary" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
            Next
          </button>
        </div>
      )}
    </div>
  )
}

// A failed load must never fall through to the empty state: "nobody abandoned
// the form" is a claim about the data, and a 502 has told us nothing.
function renderBody({ loading, error, rows, onRetry }) {
  if (loading) return <div className="text-sm text-muted" style={{ padding: 'var(--space-4)' }}>Loading…</div>
  if (error) {
    return (
      <div className="empty-state" role="alert">
        <div className="empty-state-title">Could not load abandoned trade forms</div>
        <div className="empty-state-description">{error}</div>
        <button className="btn btn-secondary" onClick={onRetry}>Try again</button>
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-title">No captures match these filters</div>
        <div className="empty-state-description">
          Somebody appears here once they give their name and email on the first step of the trade form.
        </div>
      </div>
    )
  }
  return <CapturesTable rows={rows} />
}

// Converted is the good outcome; open is still being chased; the rest are done.
// Short values stay on one line; the card scrolls sideways on narrow screens instead.
const NOWRAP = { whiteSpace: 'nowrap' }

const STATUS_BADGE = {
  open: 'badge-warning',
  converted: 'badge-success',
  unsubscribed: 'badge-neutral',
  finished: 'badge-neutral',
}

function CapturesTable({ rows }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th style={NOWRAP}>Captured</th>
          <th style={NOWRAP}>Source page</th>
          <th style={NOWRAP}>Status</th>
          <th style={NOWRAP}>Last email sent</th>
          <th style={NOWRAP}>Converted</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.capture_id}>
            <td>{row.contact_name || '-'}</td>
            <td style={NOWRAP}><a href={`mailto:${row.contact_email}`}>{row.contact_email}</a></td>
            <td className="text-sm" style={NOWRAP}>{formatDateTime(row.captured_at)}</td>
            <td className="text-sm" style={NOWRAP}>{row.source_page || '-'}</td>
            <td style={NOWRAP}><span className={`badge ${STATUS_BADGE[row.status] || 'badge-neutral'}`}>{statusLabel(row.status)}</span></td>
            <td className="text-sm" style={NOWRAP}>
              {hasLastEmail(row) ? `${lastEmailLabel(row)}, ${formatDate(d1ToIso(row.last_sent_at))}` : '-'}
            </td>
            <td className="text-sm" style={NOWRAP}>{convertedCell(row)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** The quote reference, linked to the quote when Prism gave us its id. */
function convertedCell(row) {
  if (!row.converted_reference_code) return '-'
  return <DealLink dealId={row.converted_quote_id || row.converted_reference_code} reference={row.converted_reference_code} />
}

export default AbandonedTradeForms
